import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthTokenService } from '@core/services/auth-token.service';
import { AuthService } from '@app/auth/services/auth.service';
import { catchError, finalize, map, Observable, shareReplay, switchMap, throwError } from 'rxjs';
import type { AuthRefreshResponse } from '@app/auth/models/auth-refresh-response.model';
import { UserSessionService } from '@core/services/user-session.service';

let inFlightRefresh$: Observable<AuthRefreshResponse> | null = null;

const isAuthEndpoint = (url: string): boolean => {
  try {
    return url.includes('/api/auth/');
  } catch {
    return false;
  }
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenService = inject(AuthTokenService);
  const auth = inject(AuthService);
  const session = inject(UserSessionService);
  const router = inject(Router);

  const logoutAndRedirect = (): void => {
    session.logoutLocal();
    // Don't block the interceptor chain waiting on navigation.
    void router.navigateByUrl('/login');
  };

  const accessToken = tokenService.accessToken();
  const reqWithAuth = accessToken
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    : req;

  return next(reqWithAuth).pipe(
    catchError((err: unknown) => {
      if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
        return throwError(() => err);
      }

      // Never refresh on auth endpoints (avoid loops).
      if (isAuthEndpoint(req.url)) {
        return throwError(() => err);
      }

      const refreshToken = tokenService.refreshToken();
      if (!refreshToken) {
        logoutAndRedirect();
        return throwError(() => err);
      }

      if (!inFlightRefresh$) {
        // HttpClient observables are cold: without shareReplay, each 401 retry
        // subscriber would fire a separate refresh-token request (rotation breaks).
        inFlightRefresh$ = auth.refreshToken(refreshToken).pipe(
          map((res) => {
            tokenService.setToken({
              accessToken: res.accessToken,
              expiresAt: res.accessTokenExpiresAt,
              refreshToken: res.refreshToken,
              refreshExpiresAt: res.refreshTokenExpiresAt,
            });
            return res;
          }),
          catchError((refreshErr) => {
            logoutAndRedirect();
            return throwError(() => refreshErr);
          }),
          shareReplay({ bufferSize: 1, refCount: false }),
          finalize(() => {
            inFlightRefresh$ = null;
          }),
        );
      }

      return inFlightRefresh$.pipe(
        switchMap(() => {
          const newAccessToken = tokenService.accessToken();
          if (!newAccessToken) {
            logoutAndRedirect();
            return throwError(() => err);
          }
          return next(
            req.clone({
              setHeaders: { Authorization: `Bearer ${newAccessToken}` },
            }),
          );
        }),
      );
    }),
  );
};
